import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { YoungSchemasResult } from '@/types/personality';
import { fadeInVariants, slideInVariants } from '@/lib/animations';
import { getSchemaDescriptions, getYoungSchemasRecommendations } from '@/utils/youngSchemasScoring';
import YoungSchemasResultsChart from './YoungSchemasResultsChart';
import YoungSchemasRecommendations from './YoungSchemasRecommendations';
import YoungSchemasAdvancedAnalysisButton from './YoungSchemasAdvancedAnalysisButton';
import YoungSchemasAdvancedAnalysisModal from './YoungSchemasAdvancedAnalysisModal';

interface YoungSchemasResultsProps {
  results: YoungSchemasResult;
}

export default function YoungSchemasResults({ results }: YoungSchemasResultsProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisResult, setAnalysisResult] = useState('');
  const [insightId, setInsightId] = useState('');
  
  const topSchemas = Object.entries(results.scores)
    .sort(([, scoreA], [, scoreB]) => scoreB - scoreA)
    .slice(0, 5);
  
  const schemasByDomain = {
    disconnection: ['emotional_deprivation', 'abandonment', 'mistrust_abuse', 'social_isolation', 'defectiveness'],
    impaired_autonomy: ['failure', 'dependence', 'vulnerability', 'enmeshment'],
    impaired_limits: ['entitlement', 'insufficient_self_control'],
    other_directedness: ['subjugation', 'self_sacrifice', 'approval_seeking'],
    overvigilance: ['negativity', 'emotional_inhibition', 'unrelenting_standards', 'punitiveness'],
  };
  
  const domainTitles = {
    disconnection: "Desconexão e Rejeição",
    impaired_autonomy: "Autonomia Prejudicada",
    impaired_limits: "Limites Prejudicados",
    other_directedness: "Direcionamento para o Outro",
    overvigilance: "Hipervigilância e Inibição"
  };

  const recommendations = getYoungSchemasRecommendations(results);

  const handleAnalysisComplete = (analysis: string, id: string) => {
    setAnalysisResult(analysis);
    setInsightId(id);
    setShowAnalysisModal(true);
  };
  
  return (
    <motion.div
      variants={fadeInVariants}
      initial="initial"
      animate="animate"
      className="max-w-5xl mx-auto mb-12"
    >
      <motion.h1 
        className="text-3xl font-bold mb-4"
        variants={slideInVariants.left}
      >
        Resultados do Teste de Esquemas de Young
      </motion.h1>
      
      <motion.p 
        className="text-lg text-gray-600 mb-8"
        variants={slideInVariants.left}
      >
        Seus resultados mostram seus padrões de pensamento e comportamento predominantes. 
        Entender estes esquemas pode ajudar a desenvolver estratégias para lidar com eles.
      </motion.p>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
        <TabsList>
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="schemas">Esquemas Detalhados</TabsTrigger>
          <TabsTrigger value="recommendations">Recomendações</TabsTrigger>
          <TabsTrigger value="insights">Insights</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <Card>
                <CardHeader>
                  <CardTitle>Seus Esquemas Predominantes</CardTitle>
                  <CardDescription>
                    Os esquemas com pontuações mais altas indicam padrões que podem ter maior influência em seus pensamentos e comportamentos.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {topSchemas.map(([schema, score]) => {
                      const { title, description } = getSchemaDescriptions(schema);
                      return (
                        <div key={schema} className="pb-4 border-b last:border-b-0">
                          <div className="flex justify-between mb-1">
                            <h4 className="font-medium">{title}</h4>
                            <span className="font-medium">{score.toFixed(1)}/6</span>
                          </div>
                          <p className="text-sm text-gray-600">{description}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div>
              <Card className="h-full">
                <CardHeader>
                  <CardTitle>Visualização dos Resultados</CardTitle>
                </CardHeader>
                <CardContent>
                  <YoungSchemasResultsChart results={results} />
                </CardContent>
              </Card>
            </div>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Resumo por Domínio</CardTitle>
              <CardDescription>
                Os esquemas são agrupados em cinco domínios principais que representam diferentes aspectos da personalidade.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {Object.entries(results.domainScores).map(([domain, score]) => (
                  <div key={domain} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between mb-2">
                      <h4 className="font-medium">{domainTitles[domain as keyof typeof domainTitles]}</h4>
                      <span>{score.toFixed(1)}/6</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-primary rounded-full h-2" 
                        style={{width: `${(score / 6) * 100}%`}}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
            <CardFooter>
              <Button onClick={() => setActiveTab('schemas')}>Ver detalhes por esquema</Button>
            </CardFooter>
          </Card>

          {/* Seção de Análise Avançada */}
          <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <span>Análise Avançada com IA</span>
              </CardTitle>
              <CardDescription>
                Obtenha insights psicológicos profundos sobre seus esquemas emocionais com análise personalizada por inteligência artificial, baseada na Terapia do Esquema de Jeffrey Young.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <YoungSchemasAdvancedAnalysisButton
                results={results}
                onAnalysisComplete={handleAnalysisComplete}
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="schemas" className="space-y-6">
          {Object.entries(schemasByDomain).map(([domain, schemas]) => (
            <Card key={domain}>
              <CardHeader>
                <CardTitle>{domainTitles[domain as keyof typeof domainTitles]}</CardTitle>
                <CardDescription>
                  Esquemas relacionados a {domain === 'disconnection' ? 'sentimentos de isolamento e rejeição' : 
                                         domain === 'impaired_autonomy' ? 'dificuldades em funcionar independentemente' : 
                                         domain === 'impaired_limits' ? 'dificuldades com limites e responsabilidade' :
                                         domain === 'other_directedness' ? 'foco excessivo nas necessidades dos outros' : 
                                         'padrões rígidos e auto-crítica'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {schemas.map(schema => {
                    const score = results.scores[schema] || 0;
                    const { title, description } = getSchemaDescriptions(schema);
                    const intensity = score >= 5 ? 'Alta' : score >= 3.5 ? 'Média' : 'Baixa';
                    const intensityColor = score >= 5 ? 'text-red-600' : score >= 3.5 ? 'text-amber-600' : 'text-green-600';
                    
                    return (
                      <Card key={schema} className="border-t-4" style={{
                        borderTopColor: score >= 5 ? '#ef4444' : score >= 3.5 ? '#f59e0b' : '#10b981'
                      }}>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-md">{title}</CardTitle>
                          <CardDescription>
                            Pontuação: {score.toFixed(1)}/6
                            <span className={`ml-2 font-medium ${intensityColor}`}>
                              ({intensity})
                            </span>
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm">{description}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
        
        <TabsContent value="recommendations">
          <YoungSchemasRecommendations recommendations={recommendations} results={results} />
        </TabsContent>
        
        <TabsContent value="insights" className="space-y-6">
          
          <Card>
            <CardHeader>
              <CardTitle>Padrões e Reflexões</CardTitle>
              <CardDescription>
                Análise dos seus padrões e como eles podem influenciar seu dia a dia.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-medium text-blue-800 mb-2">Impacto no Trabalho</h3>
                <p>
                  Seus esquemas predominantes podem influenciar sua vida profissional de diversas formas.
                  {topSchemas[0] && topSchemas[0][0] === 'unrelenting_standards' && 
                    " Seus altos padrões inflexíveis podem contribuir para um trabalho de alta qualidade, mas também podem levar ao perfeccionismo excessivo e burnout. Considere estabelecer limites mais realistas para si mesmo."}
                  {topSchemas[0] && topSchemas[0][0] === 'failure' && 
                    " Sua percepção de fracasso pode fazer com que você evite desafios no trabalho ou se sinta desmerecedor de reconhecimento. Desenvolver uma visão mais balanceada de suas habilidades pode ajudar."}
                </p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-medium text-green-800 mb-2">Relações Interpessoais</h3>
                <p>
                  Os esquemas afetam significativamente como você se relaciona com outras pessoas.
                  {topSchemas[0] && topSchemas[0][0] === 'abandonment' && 
                    " Seu medo de abandono pode fazer com que você busque reasseguramento constante em seus relacionamentos ou evite intimidade para se proteger de possíveis rejeições."}
                  {topSchemas[0] && topSchemas[0][0] === 'subjugation' && 
                    " Sua tendência à subjugação pode fazer com que você coloque constantemente as necessidades dos outros acima das suas, levando a ressentimentos e dificuldades em estabelecer limites saudáveis."}
                </p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-medium text-purple-800 mb-2">Autocuidado e Bem-estar</h3>
                <p>
                  Seus esquemas podem influenciar como você cuida de si mesmo e gerencia seu bem-estar.
                  {topSchemas[0] && topSchemas[0][0] === 'self_sacrifice' && 
                    " Seu esquema de autossacrifício pode fazer com que você negligencie suas próprias necessidades enquanto cuida dos outros, potencialmente levando à exaustão."}
                  {topSchemas[0] && topSchemas[0][0] === 'emotional_inhibition' && 
                    " Sua inibição emocional pode dificultar o reconhecimento e expressão de sentimentos, o que é essencial para o autocuidado efetivo."}
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Próximos Passos</CardTitle>
              <CardDescription>
                Sugestões para trabalhar com seus esquemas e promover crescimento pessoal.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                Baseado nos seus resultados, aqui estão algumas sugestões para continuar seu desenvolvimento pessoal:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <span className="font-medium">Explore a terapia focada em esquemas</span> - Esta abordagem 
                  terapêutica foi desenvolvida especificamente para trabalhar com esquemas mal-adaptativos.
                </li>
                <li>
                  <span className="font-medium">Pratique o automonitoramento</span> - Observe quando seus 
                  esquemas são ativados no dia a dia e quais situações os desencadeiam.
                </li>
                <li>
                  <span className="font-medium">Desenvolva respostas adaptativas</span> - Experimente novas 
                  formas de responder quando identificar que um esquema foi ativado.
                </li>
                <li>
                  <span className="font-medium">Combine com outras abordagens</span> - Considere técnicas 
                  como mindfulness, técnicas cognitivo-comportamentais ou práticas de autocuidado.
                </li>
              </ul>
            </CardContent>
            <CardFooter>
              <Button onClick={() => setActiveTab('recommendations')}>
                Ver recomendações personalizadas
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de Análise Avançada */}
      <YoungSchemasAdvancedAnalysisModal
        isOpen={showAnalysisModal}
        onClose={() => setShowAnalysisModal(false)}
        analysis={analysisResult}
        insightId={insightId}
      />
    </motion.div>
  );
}
